import { useEffect, useRef, useState } from "react";
import { Share2, Link2, Copy, Trash2, Sparkles, Globe, Flag, Clapperboard, Download, ExternalLink, Map as MapIcon, Radio, Music, Sun, Waves, Flame, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, API_BASE } from "../lib/api";
import { openWhatsAppJournalInvite } from "../lib/whatsapp";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Switch } from "./ui/switch";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";

const copy = async (url, msg) => {
  try {
    await navigator.clipboard.writeText(url);
    toast.success(msg, { description: url });
  } catch {
    toast.info(msg, { description: url, duration: 10000 });
  }
};

const MUSIC_MOODS = [
  ["vrolijk", "Vrolijk", Sun],
  ["chill", "Chill", Waves],
  ["episch", "Episch", Flame],
];

export const ShareDialog = ({ trip, onRefresh }) => {
  const [guestName, setGuestName] = useState("");
  const [guestEmail, setGuestEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sendingRecap, setSendingRecap] = useState(false);
  const [videoJobs, setVideoJobs] = useState(trip.video_jobs || {});
  const [videoToken, setVideoToken] = useState(trip.share_token || null);
  const [music, setMusic] = useState("vrolijk");
  const [tplBusy, setTplBusy] = useState(false);
  const [tplSlug, setTplSlug] = useState(null);
  const pollRef = useRef(null);
  useEffect(() => () => clearInterval(pollRef.current), []);
  useEffect(() => {
    api.get("/routes").then((r) => {
      const m = (r.data || []).find((x) => x.source_trip_id === trip.id);
      if (m) setTplSlug(m.slug);
    }).catch(() => {});
  }, [trip.id]);

  const toggleTemplate = async (publish) => {
    setTplBusy(true);
    try {
      const { data } = await api.post(`/trips/${trip.id}/template`, { publish });
      if (data.published) {
        setTplSlug(data.slug);
        await copy(`${window.location.origin}/routes/${data.slug}`, "Route gepubliceerd — link gekopieerd! 🗺️");
      } else {
        setTplSlug(null);
        toast.success("Route uit de bibliotheek gehaald");
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setTplBusy(false);
    }
  };
  const links = (trip.magic_links || []).filter((l) => !l.revoked);
  const isPublic = trip.is_public !== false;
  const subsCount = (trip.subscribers || []).length;

  const startVideo = async (fmt) => {
    try {
      let token = videoToken;
      if (!token) {
        const { data } = await api.post(`/trips/${trip.id}/share`);
        token = data.share_token;
        setVideoToken(token);
      }
      await api.post(`/trips/${trip.id}/video`, { format: fmt, music });
      setVideoJobs((j) => ({ ...j, [fmt]: { status: "generating" } }));
      toast.info("Reisvideo wordt gemaakt — dit duurt 1 à 2 minuten...");
      clearInterval(pollRef.current);
      pollRef.current = setInterval(async () => {
        try {
          const { data } = await api.get(`/trips/${trip.id}/video/status`);
          setVideoJobs(data.jobs || {});
          if (data.share_token) setVideoToken(data.share_token);
          const job = (data.jobs || {})[fmt] || {};
          if (job.status !== "generating") {
            clearInterval(pollRef.current);
            if (job.status === "ready") toast.success("Reisvideo klaar! 🎬 Bekijk en deel hem hieronder.");
            else toast.error(formatApiError(job.error) || "Video maken mislukt — probeer opnieuw");
            onRefresh?.();
          }
        } catch { /* volgende poll */ }
      }, 5000);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const sendRecap = async () => {
    setSendingRecap(true);
    try {
      const { data } = await api.post(`/trips/${trip.id}/recap/send`);
      toast.success(`Thuiskomst-eindverslag gemaild naar ${data.sent} volger${data.sent === 1 ? "" : "s"}! 🏁`);
      onRefresh?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSendingRecap(false);
    }
  };

  const togglePublic = async (v) => {
    try {
      await api.patch(`/trips/${trip.id}/public`, { is_public: v });
      toast.success(v ? "Trip is nu publiek zichtbaar via de deellink" : "Trip staat nu op privé");
      onRefresh?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const toggleLiveShare = async (v) => {
    try {
      await api.patch(`/trips/${trip.id}/live-share`, { enabled: v });
      toast.success(v ? "Live locatie delen staat aan — volgers zien je positie tijdens het rijden" : "Live locatie delen staat uit");
      onRefresh?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const shareLink = async () => {
    try {
      const { data } = await api.post(`/trips/${trip.id}/share`);
      await copy(`${window.location.origin}/share/${data.share_token}`, "Publieke deellink gekopieerd!");
      onRefresh?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const recapLink = async () => {
    try {
      const { data } = await api.post(`/trips/${trip.id}/share`);
      const url = `${window.location.origin}/recap/${data.share_token}`;
      await copy(url, "Samenvatting-link gekopieerd — klaar om te delen!");
      window.open(url, "_blank");
      onRefresh?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const createMagic = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (guestEmail.trim()) {
        const { data } = await api.post(`/trips/${trip.id}/invite-journal`, { name: guestName.trim() || "Gast", email: guestEmail.trim() });
        await copy(`${window.location.origin}/journal/${data.token}`, `Uitnodiging gemaild naar ${data.email} — link ook gekopieerd!`);
      } else {
        const { data } = await api.post(`/trips/${trip.id}/magic-links`, { name: guestName || "Gast" });
        await copy(`${window.location.origin}/journal/${data.token}`, `Magic link voor ${data.name} gekopieerd!`);
      }
      setGuestName("");
      setGuestEmail("");
      onRefresh?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (token) => {
    await api.delete(`/trips/${trip.id}/magic-links/${token}`);
    toast.success("Magic link ingetrokken");
    onRefresh?.();
  };

  const createMagicWhatsApp = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/trips/${trip.id}/magic-links`, { name: guestName.trim() || "Gast" });
      openWhatsAppJournalInvite(trip, data.name, data.token);
      toast.success(`WhatsApp-uitnodiging voor ${data.name} klaargezet!`);
      setGuestName("");
      setGuestEmail("");
      onRefresh?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button data-testid="share-trip-button" variant="outline" className="rounded-full active:scale-95 transition-[transform,background-color] duration-200">
          <Share2 className="w-4 h-4 sm:mr-1.5" /><span className="hidden sm:inline">Delen</span>
        </Button>
      </DialogTrigger>
      <DialogContent className="rounded-2xl max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading">Delen & toegang</DialogTitle>
          <DialogDescription className="text-xs">Deel je trip publiek of nodig familie & vrienden persoonlijk uit.</DialogDescription>
        </DialogHeader>

        <div className="bg-secondary/50 rounded-xl p-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Globe className="w-5 h-5 text-primary shrink-0" />
            <div>
              <p className="text-sm font-semibold">Publiek zichtbaar</p>
              <p className="text-xs text-muted-foreground">Iedereen met de deellink kan de trip bekijken</p>
            </div>
          </div>
          <Switch data-testid="public-toggle" checked={isPublic} onCheckedChange={togglePublic} />
        </div>

        <div className="bg-secondary/50 rounded-xl p-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Radio className="w-5 h-5 text-[#E07A5F] shrink-0" />
            <div>
              <p className="text-sm font-semibold">Live locatie delen</p>
              <p className="text-xs text-muted-foreground">Volgers zien tijdens het rijden je actuele positie live op de kaart en krijgen automatisch een mailtje wanneer je dagrit start</p>
            </div>
          </div>
          <Switch data-testid="share-live-toggle" checked={trip.share_live_location === true} disabled={!isPublic} onCheckedChange={toggleLiveShare} />
        </div>

        <Button data-testid="copy-share-link" variant="outline" onClick={shareLink} disabled={!isPublic}
          className="rounded-full w-full">
          <Link2 className="w-4 h-4 mr-2" /> Publieke deellink kopiëren
        </Button>

        <Button data-testid="recap-link-button" variant="outline" onClick={recapLink} disabled={!isPublic}
          className="rounded-full w-full border-primary/60 text-primary font-bold hover:bg-primary/10">
          <Sparkles className="w-4 h-4 mr-2" /> Trip-samenvatting delen (social)
        </Button>

        <div className="bg-secondary/50 rounded-xl p-4">
          <p className="text-sm font-semibold flex items-center gap-2"><Clapperboard className="w-4 h-4 text-primary" /> Reisvideo</p>
          <p className="text-xs text-muted-foreground mt-1 mb-3">
            Geanimeerde route over de kaart + jullie foto's, stats en muziek — perfect voor WhatsApp of Instagram.
          </p>
          <div className="flex items-center gap-1.5 mb-3" data-testid="video-music-picker">
            <Music className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
            {MUSIC_MOODS.map(([id, label, Icon]) => (
              <button key={id} type="button" data-testid={`video-music-${id}`} onClick={() => setMusic(id)}
                className={`flex items-center gap-1 rounded-full px-3 py-1 text-[11px] font-bold border transition-colors duration-200 ${
                  music === id
                    ? "bg-primary text-primary-foreground border-primary"
                    : "bg-background text-muted-foreground border-border hover:border-primary/40 hover:text-primary"
                }`}>
                <Icon className="w-3 h-3" /> {label}
              </button>
            ))}
          </div>
          <div className="space-y-2">
            {[["portrait", "Stories (9:16)"], ["square", "Vierkant (1:1)"]].map(([fmt, label]) => {
              const job = videoJobs[fmt] || {};
              if (job.status === "ready" && videoToken) {
                return (
                  <div key={fmt} className="flex items-center gap-2">
                    <a data-testid={`video-view-${fmt}`} href={`${API_BASE}/share/${videoToken}/video/${fmt}`} target="_blank" rel="noreferrer"
                      className="flex-1 rounded-full bg-primary text-primary-foreground text-xs font-bold px-4 py-2 flex items-center justify-center gap-1.5 hover:bg-[#1B4332] transition-[background-color] duration-200">
                      <ExternalLink className="w-3.5 h-3.5" /> Bekijk {label}
                    </a>
                    <a data-testid={`video-download-${fmt}`} href={`${API_BASE}/share/${videoToken}/video/${fmt}?download=1`}
                      title="Download mp4" className="w-9 h-9 rounded-full bg-secondary flex items-center justify-center hover:bg-primary/10 transition-[background-color] duration-200">
                      <Download className="w-4 h-4" />
                    </a>
                    <button data-testid={`video-redo-${fmt}`} onClick={() => startVideo(fmt)} title="Opnieuw genereren"
                      className="text-xs font-bold text-primary hover:text-[#1B4332] underline decoration-dashed transition-[color] duration-200">
                      opnieuw
                    </button>
                  </div>
                );
              }
              return (
                <Button key={fmt} data-testid={`video-make-${fmt}`} variant="outline" onClick={() => startVideo(fmt)}
                  disabled={job.status === "generating" || !isPublic} className="rounded-full w-full border-primary/40 text-primary hover:bg-primary/5">
                  <Clapperboard className="w-4 h-4 mr-2" />
                  {job.status === "generating" ? "Bezig met maken… (1-2 min)" : `Maak reisvideo — ${label}`}
                </Button>
              );
            })}
          </div>
        </div>

        <div className="bg-secondary/50 rounded-xl p-4">
          <p className="text-sm font-semibold flex items-center gap-2"><MapIcon className="w-4 h-4 text-primary" /> Route-bibliotheek</p>
          <p className="text-xs text-muted-foreground mt-1 mb-3">
            Publiceer je route (incl. overnachtingen) als template — anderen kopiëren hem met één klik naar hun eigen auto. Je verslag, foto's en kosten blijven privé.
          </p>
          {tplSlug ? (
            <div className="flex items-center gap-2">
              <a data-testid="template-view-link" href={`/routes/${tplSlug}`} target="_blank" rel="noreferrer"
                className="flex-1 rounded-full bg-primary text-primary-foreground text-xs font-bold px-4 py-2 flex items-center justify-center gap-1.5 hover:bg-[#1B4332] transition-[background-color] duration-200">
                <ExternalLink className="w-3.5 h-3.5" /> Bekijk in bibliotheek
              </a>
              <button data-testid="template-unpublish" onClick={() => toggleTemplate(false)} disabled={tplBusy}
                className="text-[11px] font-semibold text-muted-foreground hover:text-destructive underline decoration-dashed transition-[color] duration-200">
                verwijderen
              </button>
            </div>
          ) : (
            <Button data-testid="template-publish" variant="outline" onClick={() => toggleTemplate(true)} disabled={tplBusy}
              className="rounded-full w-full border-primary/40 text-primary hover:bg-primary/5">
              <MapIcon className="w-4 h-4 mr-2" /> {tplBusy ? "Publiceren..." : "Publiceer als route-template"}
            </Button>
          )}
        </div>

        <div className="bg-secondary/50 rounded-xl p-4">
          <p className="text-sm font-semibold flex items-center gap-2"><Flag className="w-4 h-4 text-primary" /> Thuiskomst-eindverslag</p>
          <p className="text-xs text-muted-foreground mt-1 mb-3">
            Mail het eindverslag (km, kWh, kosten, foto's + link naar de recap-pagina) naar iedereen die je reisverslag volgt.
            {trip.recap_email_sent && <span data-testid="recap-last-sent"> Laatst verstuurd op {trip.recap_email_sent}.</span>}
          </p>
          <Button data-testid="recap-send-button" onClick={sendRecap} disabled={sendingRecap || subsCount === 0 || !isPublic}
            className="rounded-full w-full">
            <Flag className="w-4 h-4 mr-2" />
            {subsCount === 0 ? "Nog geen volgers geabonneerd" : sendingRecap ? "Versturen…" : `Verstuur eindverslag naar ${subsCount} volger${subsCount === 1 ? "" : "s"}`}
          </Button>
        </div>

        <div className="border-t border-border pt-4">
          <p className="text-sm font-semibold flex items-center gap-2 mb-1"><Sparkles className="w-4 h-4 text-[#B5838D]" /> Nodig familie & vrienden uit</p>
          <p className="text-xs text-muted-foreground mb-3">Zij zien je dagverslagen en foto's en kunnen per dag reageren — geen account nodig. Vul een e-mailadres in en wij sturen de uitnodiging direct.</p>
          <form onSubmit={createMagic} className="space-y-2">
            <div className="flex gap-2">
              <Input data-testid="magic-name-input" value={guestName} onChange={(e) => setGuestName(e.target.value)} placeholder="bv. Oma, Tom & Lies" className="rounded-xl h-10" />
              <Input data-testid="magic-email-input" type="email" value={guestEmail} onChange={(e) => setGuestEmail(e.target.value)} placeholder="E-mail (optioneel)" className="rounded-xl h-10" />
            </div>
            <Button data-testid="magic-create-button" type="submit" disabled={busy} className="rounded-full bg-[#B5838D] hover:bg-[#8E5D67] w-full">
              {guestEmail.trim() ? "Verstuur uitnodiging per e-mail" : "Maak link om zelf te delen"}
            </Button>
            <Button data-testid="magic-whatsapp-button" type="button" onClick={createMagicWhatsApp} disabled={busy}
              className="rounded-full bg-[#25D366] hover:bg-[#1fb457] text-white w-full">
              <MessageCircle className="w-4 h-4 mr-1.5" /> Nodig uit via WhatsApp
            </Button>
          </form>
        </div>

        <div className="space-y-2 max-h-[30vh] overflow-y-auto">
          {links.map((l) => (
            <div key={l.token} data-testid={`magic-link-row-${l.token}`} className="bg-secondary/50 rounded-xl p-3 flex items-center justify-between gap-2">
              <div className="min-w-0">
                <p className="text-sm font-semibold truncate">{l.name}</p>
                {l.email && <p className="text-xs text-muted-foreground truncate">{l.email}</p>}
              </div>
              <div className="flex gap-1 shrink-0">
                <button data-testid={`magic-whatsapp-${l.token}`} onClick={() => openWhatsAppJournalInvite(trip, l.name, l.token)}
                  className="p-2 rounded-full hover:bg-[#25D366]/10 text-[#25D366] transition-[background-color] duration-200" title="Stuur via WhatsApp">
                  <MessageCircle className="w-4 h-4" />
                </button>
                <button data-testid={`magic-copy-${l.token}`} onClick={() => copy(`${window.location.origin}/journal/${l.token}`, `Link voor ${l.name} gekopieerd!`)}
                  className="p-2 rounded-full hover:bg-secondary text-primary transition-[background-color] duration-200" title="Kopieer link">
                  <Copy className="w-4 h-4" />
                </button>
                <button data-testid={`magic-revoke-${l.token}`} onClick={() => revoke(l.token)}
                  className="p-2 rounded-full hover:bg-red-50 text-red-500 transition-[background-color] duration-200" title="Intrekken">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
          {links.length === 0 && <p className="text-xs text-muted-foreground">Nog geen magic links aangemaakt.</p>}
        </div>
      </DialogContent>
    </Dialog>
  );
};
